import { isToday, isTomorrow, isYesterday } from "date-fns";
import { MailStatus, OrderStatus, palette, RowType, SortDirection } from "./common/constants";
import { CorrectionType, TestResultStatus, TestType } from "./enums";
import { v4 } from "uuid";
import api from "./api";

const resolveTimeZone = (explicitTimeZone) => {
    if (explicitTimeZone) {
        return explicitTimeZone;
    }
    if (typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
        try {
            const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();
            if (timeZone) {
                return timeZone;
            }
        }
        catch (error) {
            // Fall back to default when timezone cannot be resolved.
        }
    }
    return "UTC";
};

export function groupMessagesByDate(messages, options = {}) {
    if (!Array.isArray(messages) || messages.length === 0) {
        return {};
    }

    const { timeZone: explicitTimeZone, reverse = false } = options;
    const timeZone = resolveTimeZone(explicitTimeZone);
    let formatter = null;

    try {
        formatter = new Intl.DateTimeFormat("en-CA", {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    }
    catch (error) {
        // Formatter is not available for the provided timezone; fall back to manual formatting.
    }

    const fallbackFormat = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const normalizedMessages = messages.reduce((acc, message) => {
        if (!message || !message.created_at) {
            return acc;
        }
        const parsedDate = new Date(message.created_at);
        if (Number.isNaN(parsedDate.getTime())) {
            return acc;
        }
        acc.push({ message, date: parsedDate });
        return acc;
    }, []);

    normalizedMessages.sort((a, b) => a.date - b.date);

    const grouped = normalizedMessages.reduce((groups, { message, date }) => {
        let formattedDate = null;
        if (formatter) {
            formattedDate = formatter.format(date);
        } else if (timeZone && typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
            try {
                const zonedDate = new Date(date.toLocaleString("en-US", { timeZone }));
                formattedDate = fallbackFormat(zonedDate);
            }
            catch (error) {
                formattedDate = fallbackFormat(date);
            }
        } else {
            formattedDate = fallbackFormat(date);
        }

        const key = `${formattedDate}T00:00:00`;
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(message);
        return groups;
    }, {});

    if (!reverse) {
        return grouped;
    }

    const reversedGroups = {};
    const keys = Object.keys(grouped);
    for (let i = keys.length - 1; i >= 0; i -= 1) {
        const key = keys[i];
        reversedGroups[key] = grouped[key];
    }
    return reversedGroups;
}

export function capitalize(string) {
    // Verifica se la stringa è vuota
    if (!string) {
        return "";
    }

    // Dividi la stringa in un array di parole
    const words = string.split(' ');

    // Capitalizza ogni parola nell'array
    const capitalizedWords = words.map(word => {
        // Capitalizza la prima lettera di ogni parola
        return word.charAt(0).toUpperCase() + word.slice(1);
    });

    // Unisci le parole capitalizzate in una stringa e restituiscila
    return capitalizedWords.join(' ');
}

export function filterArray(arr, str) {
    return arr.filter(item => {
        const words = str
            .toLowerCase()
            .split(' ')
            .filter(word => word.length > 2); // Dividi la stringa in parole lunghe più di 3 lettere
        return words.every(word => item.toLowerCase().includes(word)); // Controlla se tutte le parole sono incluse nella stringa
    });
}

export function sortFilteredArray(arr, str) {
    return arr.sort((a, b) => {
        const startsWithStrA = a.toLowerCase().startsWith(str.toLowerCase());
        const startsWithStrB = b.toLowerCase().startsWith(str.toLowerCase());

        // Se entrambi iniziano con la stringa specificata, li ordina normalmente
        if (startsWithStrA && startsWithStrB) {
            return a.localeCompare(b);
        }
        // Se solo uno inizia con la stringa, lo mette prima
        else if (startsWithStrA) {
            return -1;
        } else if (startsWithStrB) {
            return 1;
        }
        // Altrimenti, li ordina normalmente
        else {
            return a.localeCompare(b);
        }
    });
}

// Funzione principale
export function filterAndSortArray(arr, str) {
    const filteredArr = filterArray(arr, str);
    return sortFilteredArray(filteredArr, str);
}

export function formatPrice(num, toFixed = 2) {
    if (!num && toFixed === 2) {
        return "00,00";
    }
    if (!num && toFixed === 0) {
        return "0";
    }

    // Converti il numero in una stringa con due cifre decimali
    let formattedNum = parseFloat(num).toFixed(toFixed).toString().replace('.', ',');

    // Dividi la parte intera dalla parte decimale
    let parts = formattedNum.split(',');

    // Formatta la parte intera aggiungendo i punti come separatore ogni 3 cifre
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    // Unisci le due parti e restituisci il risultato
    return parts.join(',');
}

export function splitArray(array, nParts) {
    var subArrays = []
    for (let i = 0; i < nParts; i++) {
        subArrays.push([])
    }
    for (let j = 0; j < array.length; j++) {
        const index = j % subArrays.length; // Calcola l'indice del colore utilizzando l'operatore modulo
        subArrays[index].push(array[j])
    }

    return subArrays
}

export function chunkArray(array, chunkSize) {
    const result = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
    }
    return result;
}

export function hexAlpha(color, alpha) {
    // Converti la percentuale alpha in un valore compreso tra 0 e 1
    const _alpha = alpha / 100;

    // Calcola il valore alpha in esadecimale
    const alphaHex = Math.round(_alpha * 255).toString(16).toUpperCase();

    // Assicurati che il valore esadecimale abbia due cifre
    const alphaPadded = alphaHex.padStart(2, '0');

    // Restituisci il colore esadecimale con alpha
    return color + alphaPadded;
}

export function formatDate(dateString, compact = false, includeWeekDay = true) {
    let date = new Date(dateString)
    // Array dei nomi dei giorni della settimana
    const daysOfWeek = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

    // Array dei nomi dei mesi
    const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

    // Ottieni il giorno della settimana, il giorno del mese e il mese dalla data
    const dayOfWeek = compact ? daysOfWeek[date.getDay()].substring(0, 3) : daysOfWeek[date.getDay()];
    const dayOfMonth = date.getDate();
    const month = compact ? months[date.getMonth()].substring(0, 3) : months[date.getMonth()];

    if (isToday(date)) {
        return 'Oggi'
    }
    else if (isTomorrow(date)) {
        return 'Domani'
    }
    else if (isYesterday(date)) {
        return 'Ieri'
    }

    // Formatta la data nel formato desiderato
    const formattedDate = includeWeekDay ? `${dayOfWeek} ${dayOfMonth} ${month}` : `${dayOfMonth} ${month}`

    return formattedDate;
}

export function formatDateV2(timestamp, options, lng = "it", applyTz = true, forceNumeric = true) {
    try {
        const date = new Date(timestamp)
        let userDate = date

        if (applyTz) {
            const offset = date.getTimezoneOffset()
            userDate = new Date(date.getTime() - (offset * 60 * 1000));
        }

        if (!forceNumeric) {
            if (isToday(userDate)) {
                return 'Oggi'
            }
            else if (isTomorrow(userDate)) {
                return 'Domani'
            }
            else if (isYesterday(userDate)) {
                return 'Ieri'
            }
        }

        var baseOptions = { month: 'long', day: 'numeric' };
        baseOptions = options ? options : baseOptions
        return userDate.toLocaleDateString(lng, baseOptions)
    }
    catch (e) {
        console.error(`${timestamp} generated error: ${e.message}`);
        return "Invalid Date"
    }
}

export function formatTimeV2(timestamp, options, lng = "it", applyTz = true) {
    try {
        if (!timestamp) {
            return "";
        }
        // Estrai solo l'ora dal timestamp e sostituisci la data con quella attuale
        const currentDate = new Date(timestamp).toISOString().split('T')[0]; // Ottieni la data attuale in formato yyyy-mm-dd
        const timePart = timestamp.split('T')[1]; // Estrai la parte dell'ora dal timestamp
        timestamp = `${currentDate}T${timePart}`; // Ricostruisci il timestamp con la data attuale

        // Convert AM/PM format to 24-hour format if necessary
        if (timestamp.includes('AM') || timestamp.includes('PM')) {
            const [datePart, timePart] = timestamp.split('T');
            const [time, period] = timePart.split(' ');
            let [hours, minutes, seconds] = time.split(':').map(Number);

            if (period === 'PM' && hours < 12) {
                hours += 12;
            } else if (period === 'AM' && hours === 12) {
                hours = 0;
            }

            // Ricostruisci il timestamp con padding corretto
            timestamp = `${datePart}T${String(hours).padStart(2, '0')}:${String(minutes || 0).padStart(2, '0')}:${String(seconds || 0).padStart(2, '0')}`;
        }

        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
            console.error(`${timestamp} generated error: Invalid Date Format`);
        }

        var userDate = date;
        if (applyTz) {
            const offset = date.getTimezoneOffset();
            userDate = new Date(date.getTime() - (offset * 60 * 1000));
        }
        var baseOptions = { hour: '2-digit', minute: '2-digit' };
        baseOptions = options ? options : baseOptions;
        return userDate.toLocaleTimeString(lng, baseOptions);
    }
    catch (e) {
        console.error(`${timestamp} generated error: ${e.message}`);
        return "--"
    }
}

export function formatTime(dateString) {
    let date = new Date(dateString)
    // Ottieni le ore e i minuti dalla data
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Aggiungi uno zero iniziale se i minuti sono inferiore a 10
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    // Formatta l'ora nel formato HH:mm
    const formattedTime = `${hours}:${formattedMinutes}`;

    return formattedTime;
}

export function getMonthName(dateString, compact = false) {
    const date = new Date(dateString)
    const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    return compact ? months[date.getMonth()].substring(0, 3) : months[date.getMonth()];
}

export function getDayName(dateString, compact = false) {
    const date = new Date(dateString)
    const daysOfWeek = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return compact ? daysOfWeek[date.getDay()].substring(0, 3) : daysOfWeek[date.getDay()];
}

/**
 * Calcola il numero della settimana dell'anno per una data specifica.
 * @param {Date} date - La data di input.
 * @returns {number} - Il numero della settimana dell'anno.
 */
export function getWeekNumber(date) {
    // Copia la data per evitare modifiche all'oggetto originale
    const currentDate = new Date(date.getTime());

    // Imposta il primo giorno della settimana come lunedì
    const dayOfWeek = (currentDate.getDay() + 6) % 7; // 0 (domenica) diventa 6, 1 (lunedì) diventa 0, ecc.

    // Sposta la data al giovedì della settimana corrente
    currentDate.setDate(currentDate.getDate() - dayOfWeek + 3);

    // Calcola il primo giorno dell'anno
    const firstThursday = new Date(currentDate.getFullYear(), 0, 4);
    const firstWeekStart = new Date(firstThursday.getTime());
    firstWeekStart.setDate(firstThursday.getDate() - ((firstThursday.getDay() + 6) % 7));

    // Calcola il numero della settimana
    const weekNumber = Math.ceil((currentDate - firstWeekStart) / (7 * 24 * 60 * 60 * 1000)) + 1;

    return weekNumber;
}

export function hourDiff(dateString1, dateString2) {
    // Converti le stringhe in oggetti Date
    const date1 = new Date(dateString1);
    const date2 = new Date(dateString2);

    // Calcola la differenza in millisecondi
    const msDiff = Math.abs(date1 - date2);

    // Converti la differenza in ore
    const hDiff = Math.floor(msDiff / (1000 * 60 * 60));

    return hDiff;
}

export function calcLastUpdate(dateString, compact) {
    let now = new Date();
    let date = new Date(dateString);

    // let offset = date.getTimezoneOffset();

    // Aggiungi l'offset al timestamp della data specificata
    // let localDate = new Date(date.getTime() - (offset * 60 * 1000));

    const msDiff = Math.abs(now - date);

    const sDiff = parseInt(msDiff / (1000))
    const mDiff = parseInt(msDiff / (1000 * 60))
    const hDiff = parseInt(msDiff / (1000 * 60 * 60))
    const dDiff = parseInt(msDiff / (1000 * 60 * 60 * 24))
    const wDiff = parseInt(msDiff / (1000 * 60 * 60 * 24 * 7))

    var result = compact ? `${wDiff}s` : `${wDiff} ${wDiff === 1 ? 'settimana' : 'settimane'} fa`

    if (wDiff < 1) {
        result = compact ? `${dDiff}g` : `${dDiff} ${dDiff === 1 ? 'giorno' : 'giorni'} fa`
        if (hDiff < 24) {
            result = compact ? `${hDiff}h` : `${hDiff} ${hDiff === 1 ? 'ora' : 'ore'} fa`
            if (hDiff < 1) {
                result = compact ? `${mDiff}m` : `${mDiff} ${mDiff === 1 ? 'minuto' : 'minuti'} fa`
                if (mDiff < 1) {
                    result = compact ? `${sDiff}sec` : `${sDiff} ${sDiff === 1 ? 'secondo' : 'secondi'} fa`
                }
            }
        }
    }

    return result;
}

/**
 * This function removes the duplicates from an array checking the id field.
 * @param {*} array 
 * @returns the array with no duplicates objects.
 */
export const dropDuplicates = (array) => {
    const uniqueObjects = {};
    const result = [];

    // Itera sull'array originale
    array.forEach(obj => {
        // Verifica se l'oggetto con lo stesso id è già presente nell'oggetto uniqueObjects
        if (!uniqueObjects[obj.id]) {
            // Se non è presente, aggiungi l'oggetto all'oggetto uniqueObjects e al risultato
            uniqueObjects[obj.id] = true;
            result.push(obj);
        }
    });

    return result;
};

export function formatFileSize(byteSize) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let size = byteSize;
    let i = 0;

    while (size >= 1000 && i < units.length - 1) {
        size /= 1000;
        i++;
    }

    return size.toFixed(2) + ' ' + units[i];
}

export function getLocaleISOString() {
    const currentDate = new Date().toLocaleDateString()
    const currentTime = new Date().toLocaleTimeString()
    const components = currentDate.split('/')
    const isoString = `${components[2]}-${components[1].padStart(2, '0')}-${components[0].padStart(2, '0')}T${currentTime}`
    return isoString
}

export function toLetter(value) {
    return (value + 9).toString(36)
}

export function emptyOrNull(string) {
    return string === null || string.trim() === ''
}

export function getDefaultSection() {
    return {
        name: "",
        position: 1,
        rows: [
            {
                content: "",
                type: RowType.Text,
                style: {}
            }
        ]
    }
}

export function getDefaultRow() {
    return {
        content: "",
        type: RowType.Text,
        style: {}
    }
}


export function hasProperty(object, property, value) {
    return object && object[property] && object[property] === value
}

export function secondsToHours(seconds, compact = false) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    let s = `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
    if (!compact) {
        s += ` ${String(remainingSeconds).padStart(2, '0')}s`
    }
    return s;
}

export async function checkImage(url) {
    const res = await fetch(url, { mode: "no-cors" });
    const buff = await res.blob();
    return buff.type.startsWith('image/')

}

export function UTCtoLocal(dateString) {
    let date = new Date(dateString)
    const offset = date.getTimezoneOffset()
    return new Date(date.getTime() - (offset * 60 * 1000))
}


export function getDateArray(startDate, endDate) {
    const dateArray = [];
    let currentDate = new Date(startDate);
    endDate = new Date(endDate)

    while (currentDate <= endDate) {
        dateArray.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dateArray;
}

export function fillEmptyDays(data) {
    // si presuppone che data abbia un campo "date" (ISOString) e che sia ordinato per date
    if (data.length === 0 || data.length === 1) {
        return data
    }

    const min = data[0].date
    const max = data[data.length - 1].date

    const dateArrays = getDateArray(min, max).map(d => d.toISOString().split("T")[0])
    const result = [data[0]]
    let j = 1;
    for (let i = 1; i < dateArrays.length - 1 && j < data.length - 1; i++) {
        if (dateArrays[i] === data[j].date) {
            result.push(data[j])
            j++
        } else {
            result.push({ ...result[i - 1], date: dateArrays[i] })
        }
    }
    result.push(data[data.length - 1])
    return result
}

export function getMonthsArrays(startDate, endDate) {
    const dateArray = [];
    let currentDate = new Date(startDate);
    currentDate.setDate(15)
    endDate = new Date(endDate)

    while (currentDate <= endDate) {
        dateArray.push(new Date(currentDate));
        currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return dateArray;
}

export function fillEmptyMonths(data) {
    // si presuppone che data abbia un campo "date" (ISOString) e che sia ordinato per date
    if (data.length === 0 || data.length === 1) {
        return data
    }

    const min = data[0].date
    const max = data[data.length - 1].date

    const dateArrays = getMonthsArrays(min, max).map(d => d.toISOString().split("T")[0])
    const result = [data[0]]
    let j = 1;

    const getMonth = (s) => {
        return s.split("-").slice(0, 2).join("-")
    }

    for (let i = 1; i < dateArrays.length - 1 && j < data.length - 1; i++) {
        if (getMonth(dateArrays[i]) === getMonth(data[j].date)) {
            result.push(data[j])
            j++
        } else {
            result.push({ ...result[i - 1], date: dateArrays[i] })
        }
    }
    result.push(data[data.length - 1])
    return result
}


/* -------------------------------------------------------------------------- */
/*                                 TEST UTILS                                 */
/* -------------------------------------------------------------------------- */
export function isTestValid(name, module, lesson, innerTests) {
    let areInnerTestValid = true
    const isModuleValid = module && module.id > 0
    const isLessonValid = true

    for (let i = 0; i < innerTests.length; i++) {
        const innerTest = innerTests[i];
        if (innerTest.isValid === false) {
            areInnerTestValid = false;
        } else {
            // Validazione dettagliata per capire il motivo
            if (!innerTest.testType) {
                areInnerTestValid = false;
            } else if (!innerTest.text || innerTest.text.trim() === "") {
                areInnerTestValid = false;
            } else if (!innerTest.answers || innerTest.answers.length === 0) {
                areInnerTestValid = false;
            } else if ((innerTest.testType === 'SingleChoice' || innerTest.testType === 'MultipleChoice')) {
                const atLeastOneCorrect = innerTest.answers.some(a => a.isCorrect);
                const atLeastOneIncorrect = innerTest.answers.some(a => !a.isCorrect);
                const allAnswersHaveText = innerTest.answers.every(a => a.text && a.text.trim() !== "");
                if (!atLeastOneCorrect) {
                    areInnerTestValid = false;
                } else if (!atLeastOneIncorrect) {
                    areInnerTestValid = false;
                } else if (!allAnswersHaveText) {
                    areInnerTestValid = false;
                }
            } else if (innerTest.testType === 'TrueFalse') {
                const allAnswersHaveText = innerTest.answers.every(a => a.text && a.text.trim() !== "");
                const allAnswersHaveValue = innerTest.answers.every(a => a.value !== null && a.value !== undefined);
                if (!allAnswersHaveText) {
                    areInnerTestValid = false;
                } else if (!allAnswersHaveValue) {
                    areInnerTestValid = false;
                }
            } else if (innerTest.testType === 'TextCompletion') {
                if (!innerTest.words || innerTest.words.length === 0) {
                    areInnerTestValid = false;
                } else if (!innerTest.words.some(w => w.hidden === true)) {
                    areInnerTestValid = false;
                }
            } else {
                // Se passa tutti i controlli
            }
        }
    }

    return {
        isValid: name !== null && name !== "" && areInnerTestValid && isModuleValid && isLessonValid,
        isNameEmpty: !name,
        areInnerTestValid,
        isModuleValid,
        isLessonValid
    }
}

export function isTestChoiceValid(text, answers) {
    let atLeastOneIsCorrect = false
    let atLeastOneIsIncorrect = false
    let isAnswerTextValid = true

    for (const a of answers) {
        if (a.isCorrect) {
            atLeastOneIsCorrect = true
        } else if (!a.isCorrect) {
            atLeastOneIsIncorrect = true
        }

        if (!a.text) {
            isAnswerTextValid = false
        }
    }

    return {
        isValid: text !== "" && answers.length > 0 && atLeastOneIsCorrect && atLeastOneIsIncorrect && isAnswerTextValid,
        noQuestion: text === "",
        noAnswers: !answers || answers.length === 0,
        atLeastOneIsCorrect,
        atLeastOneIsIncorrect,
        isAnswerTextValid
    }
}

export function isTestTrueFalseValid(answers) {
    let everyAnswerHasSolution = true
    let isAnswerTextValid = true

    for (const a of answers) {
        if (!a.text) {
            isAnswerTextValid = false
        }

        if (a.value === null || a.value === undefined) {
            everyAnswerHasSolution = false
        }
    }

    return {
        isValid: everyAnswerHasSolution && answers.length > 0 && isAnswerTextValid,
        everyAnswerHasSolution,
        isAnswerTextValid
    }
}

export function isTestTextCompletionValid(words) {
    let atLeastOneHiddenWord = false

    for (const w of words) {
        if (w.hidden === true) {
            atLeastOneHiddenWord = true
        }
    }

    return {
        isValid: words.length > 0 && atLeastOneHiddenWord,
        atLeastOneHiddenWord,
        emptyText: words.length === 0
    }
}


export function getDefaultTest() {
    return {
        name: "",
        description: "",
        module: null,
        lesson: null,
        tags: [],
        can_be_retried: false,
        expires_at: null,
        expires_in: null,
        published_at: null,
        correction_type: CorrectionType.Solutions,
        success_threshold: 1,
        content: {
            innerTests: [
                {
                    testType: TestType.SingleChoice,
                    text: "",
                    tags: [],
                    answers: [{
                        id: v4(),
                        text: "",
                        isCorrect: false
                    }, {
                        id: v4(),
                        text: "",
                        isCorrect: false
                    }]
                }
            ]
        }
    }
}


export function isTestCompleted(innerTests) {
    let completed = 0
    for (const it of innerTests) {
        for (const r of it.responses) {
            if (r.value) {
                completed += 1
                break
            }
        }
    }
    if (completed === innerTests.length) {
        return TestResultStatus.Passed
    } else if (completed > 0 && completed < innerTests.length) {
        return TestResultStatus.Partial
    } else {
        return TestResultStatus.NotCompleted
    }
}

export function getSingleWords(text) {
    return text.split(/[\s\n]+/)
        .filter(w => w !== " " && w !== "")
        .map(s => {
            const markdown = s.slice()
            const clean = s.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]|<\/?u>|x20;/g, '')
            return {
                text: clean,
                markdown,
                hidden: false,
            }
        })
}

export function wrapWordsWithAsterisks(text) {
    // Regex per trovare il testo tra * o ** asterischi
    return text.replace(/(\*+)(.*?)\1/g, (match, asterisks, content) => {
        // Wrappare ogni parola con lo stesso numero di asterischi
        const wrappedWords = content.split(' ').map(word => `${asterisks}${word}${asterisks}`).join(' ');
        return wrappedWords;
    });
}

export function getInitials(input) {
    const words = input.split(' ');
    const initials = words.map(word => word.charAt(0)).join('');
    return initials;
}

export function getRandomColor() {
    const randomIndex = Math.floor(Math.random() * palette.length);
    return palette[randomIndex];
}


export const sortUser = (a, b, orderBy, sortDirection) => {
    const { value } = orderBy;

    const fieldA = a[value];
    const fieldB = b[value];
    if (value === 'created_at') {
        const dateA = new Date(fieldA);
        const dateB = new Date(fieldB);
        return sortDirection === SortDirection.Ascending
            ? dateA - dateB
            : dateB - dateA;
    }
    if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === SortDirection.Ascending
            ? fieldA.localeCompare(fieldB) // Crescente
            : fieldB.localeCompare(fieldA); // Decrescente
    }
    if (typeof fieldA === 'number' && typeof fieldB === 'number') {
        return sortDirection === SortDirection.Ascending
            ? fieldA - fieldB
            : fieldB - fieldA;
    }
    return 0;
};

export function range(min, max, step = 1) {
    const result = [];
    for (let i = min; i <= max; i += step) {
        result.push(i);
    }
    return result;
}

export function formatSeconds(seconds) {
    if (seconds <= 0 || !seconds) {
        return '0s'
    }

    const thresholdSeconds = 12 * 60 * 60;
    let days = Math.floor(seconds / (24 * 60 * 60));
    let remainingSeconds = seconds % (24 * 60 * 60);

    if (days === 0 && seconds > thresholdSeconds) {
        days = 1;
        remainingSeconds = seconds - thresholdSeconds;
    }

    const totalMinutes = Math.floor(remainingSeconds / 60);
    const secs = Math.floor(remainingSeconds % 60);
    const mins = totalMinutes % 60;
    const hrs = Math.floor(totalMinutes / 60);

    if (days > 0) {
        return `${days}g ${hrs}h ${mins}m ${secs}s`;
    } else if (hrs > 0) {
        return `${hrs}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
        return `${mins}m ${secs}s`;
    } else {
        return `${secs}s`;
    }
}

export function getLocalDate(noTimezoneDate) {
    let date = new Date(noTimezoneDate)
    const offset = date.getTimezoneOffset()
    return new Date(date.getTime() - (offset * 60 * 1000))
}

export const formatSecondsDuration = (seconds) => {
    if (!seconds) return '00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    } else {
        return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
};

export function includesCaseInsensitive(text, searchString) {
    const regex = new RegExp(searchString, 'i');
    return regex.test(text);
}

export const getLogPhrase = (log) => {
    const { action, user, object_type, object, data } = log;

    let verb = ""
    let obj = ""
    let predicate = null
    let color = 'var(--text-color)'

    switch (action) {
        case 'created':
            if (user) {
                verb = "ha creato"
            }
            else {
                verb = "è stato creato"
            }
            color = 'var(--sf-green)'
            break;
        case 'updated':
            if (user) {
                verb = "ha modificato"
            }
            else {
                verb = "è stato modificato"
            }
            color = 'var(--sf-purple)'
            break;
        case 'completed':
            verb = "ha completato"
            color = 'var(--sf-green)'
            break;
        case 'deleted':
            verb = "ha eliminato"
            color = 'var(--sf-red)'
            break;
        case 'uploaded':
            verb = "ha caricato"
            color = 'var(--sf-blue)'
            break;
        case 'failed':
            verb = "ha ricevuto un errore su"
            color = 'var(--sf-red)'
            break;
        case 'added_note':
            verb = "ha aggiunto una nota a"
            color = 'var(--sf-yellow)'
            break;
        case 'create_refund':
            verb = "ha emesso un rimborso per"
            color = 'var(--sf-orange)'
            predicate = `${data.product_refunds?.map(r => r.amount).reduce((a, b) => a + b, 0) / 100}€`
            break;
        case 'refunded':
            verb = "ha ricevuto il rimborso per"
            predicate = `${data.refund.amount / 100}€`
            color = 'var(--sf-green)'
            break;
        default:
            verb = ""
            break;
    }

    switch (object_type) {
        case 'test':
            obj = "il test"
            predicate = object.name
            break;
        case 'module':
            obj = "il modulo"
            predicate = object.name
            break;
        case 'lesson':
            obj = "la lezione"
            predicate = object.name
            break;
        case 'user':
            obj = "l'utente"
            predicate = object.name
            break;
        case 'order':
            obj = "l'ordine"
            break;
        case 'course':
            obj = "il corso"
            predicate = object.name
            break;
        case 'exercise':
            obj = "l'esercizio"
            predicate = object.name
            break;
        case 'material':
            obj = "il materiale"
            predicate = object.name
            break;
        case 'communication':
            obj = "l'annuncio"
            break;
        case 'carrello':
            obj = "il carrello"
            break;
        case 'user_application':
            obj = "il lead"
            break;
        default:
            obj = ""
            break;
    }

    return { name: user ? `${user?.name} ${user?.surname}` : '', verb, obj, predicate, color }
}

export const groupLogsByDate = (logs) => {
    // Raggruppa i log per data (YYYY-MM-DD) in fuso orario locale
    const grouped = (logs || []).reduce((acc, log) => {
        const date = UTCtoLocal(log.created_at);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;

        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(log);
        return acc;
    }, {});

    // Ordina le date dalla più recente alla meno recente
    const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    // Ricostruisci un oggetto usando l'ordine desiderato (in JS l'ordine di inserimento è preservato)
    const ordered = {};
    for (const key of sortedKeys) {
        ordered[key] = grouped[key];
    }

    return ordered;
}

export const getOrderStatusBadge = (status) => {
    switch (status) {
        case OrderStatus.Pending:
            return {
                label: "In Attesa",
                color: "var(--sf-gray)"
            }
        case OrderStatus.Processing:
            return {
                label: "In elaborazione",
                color: "var(--sf-yellow)"
            }
        case OrderStatus.Succeeded:
            return {
                label: "Completato",
                color: "var(--sf-light-green)"
            }
        case OrderStatus.Failed:
            return {
                label: "Errore",
                color: "var(--sf-red)"
            }
        case OrderStatus.Refunded:
            return {
                label: "Rimborsato",
                color: "var(--sf-blue)"
            }
        case OrderStatus.WaitingForRefund:
            return {
                label: "Elaborazione rimborso",
                color: "var(--sf-yellow)"
            }
        case OrderStatus.PartiallyRefunded:
            return {
                label: "Parzialmente rimborsato",
                color: "var(--sf-orange)"
            }
        case OrderStatus.RefundFailed:
            return {
                label: "Rimborso fallito",
                color: "var(--sf-red)"
            }
        case OrderStatus.Unpaid:
            return {
                label: "Non pagato",
                color: "var(--sf-red)"
            }
        case OrderStatus.PartiallyPaid:
            return {
                label: "Parzialmente pagato",
                color: "var(--sf-orange)"
            }
        case OrderStatus.Abandoned:
            return {
                label: "Abbandonato",
                color: "black"
            }
        case OrderStatus.Canceled:
            return {
                label: "Annullato",
                color: "var(--sf-gray)"
            }
        case OrderStatus.RequiresAction:
            return {
                label: "Richiede Azione",
                color: "var(--sf-orange)"
            }
        default: return null
    }
}

export function deepEqual(a, b) {
    if (a === b) return true;

    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
        return false;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!deepEqual(a[key], b[key])) return false;
    }

    return true;
}


export const getMailStatusBadge = (status) => {
    switch (status) {
        case MailStatus.Scheduled:
            return {
                label: "Programmata",
                color: "var(--sf-gray)"
            }
        case MailStatus.Sent:
            return {
                label: "Inviata",
                color: "var(--sf-light-green)"
            }
        case OrderStatus.Failed:
            return {
                label: "Errore",
                color: "var(--sf-red)"
            }
        case MailStatus.Cancelled:
            return {
                label: "Annullata",
                color: "black"
            }
        default: return null
    }
}

export function validateEmail(email) {
    if (email.length > 0) {
        return email.match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    }
    else {
        return true
    }
}

const uploadChunk = async ({ url, chunk, partNumber, onProgress }) => {
    try {
        const response = await fetch(url, {
            method: 'PUT',
            body: chunk,
        });

        if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
        }

        // La risposta di un PUT verso S3/Spaces non ha un corpo JSON, ma l’ETag è nei headers
        const etag = response.headers.get('Etag')?.replaceAll('"', '');
        if (!etag) throw new Error('ETag not found');

        // `fetch` non supporta `onUploadProgress` nativamente, ma possiamo simulare il progresso solo per chunk interi
        onProgress?.(100, partNumber);

        return { PartNumber: partNumber, ETag: etag };
    } catch (error) {
        console.error(`Error uploading chunk ${partNumber}:`, error);
        throw new Error(`Failed to upload chunk ${partNumber}`);
    }
};

export const uploadMultipart = async (file, subdirectory, add_timestamp = true, onGlobalProgress) => {
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const parts = [];

    let initData = {
        filename: file.name,
    }

    if (subdirectory) {
        initData.subdirectory = subdirectory;
    }

    const { uploadId, key } = await api.post(`/admin/upload/initiate?add_timestamp=${add_timestamp}`, initData);

    let uploadedTotal = 0;

    for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(file.size, start + chunkSize);
        const chunk = file.slice(start, end);
        const partNumber = i + 1;
        const baseUploaded = uploadedTotal;

        const { url } = await api.get(`/admin/upload/presigned-url`, {
            params: {
                upload_id: uploadId,
                key: key,
                part_number: partNumber
            }
        });

        const part = await uploadChunk({
            url,
            chunk,
            partNumber,
            onProgress: (percent) => {
                // Calcolo progresso del singolo chunk in byte
                const currentUploaded = (percent / 100) * chunk.size;
                const globalProgress = Math.min(
                    Math.round(((baseUploaded + currentUploaded) / file.size) * 100),
                    100
                );
                onGlobalProgress?.(globalProgress);
            }
        });

        // Una volta finito il chunk, aggiungilo ai bytes totali caricati
        uploadedTotal = baseUploaded + chunk.size;

        parts.push(part);
    }

    const { url: finalUrl } = await api.post('/admin/upload/complete', {
        uploadId,
        key,
        parts,
    });

    return { url: finalUrl };
};

export function formatNumber(number) {
    const numericValue = typeof number === 'number' ? number : Number(number);
    if (!Number.isFinite(numericValue)) {
        return (0).toLocaleString('it-IT');
    }
    return numericValue.toLocaleString('it-IT');
}

export function isNumberValid(num, allowZero = false) {
    if (Math.abs(num) === Infinity || isNaN(num) || num === null || num === undefined) {
        return false;
    }
    if (!allowZero && num === 0) {
        return false;
    }
    return true;
}

export const isNullOrEmpty = (str) => {
    return str === null || str === undefined || str.trim() === '';
}